const WINDOW_MS = 60_000;
const SHARED_QUOTA = 85;
const BURST = 25;
const MIN_COOLDOWN_MS = 5_000;
const MAX_COOLDOWN_MS = 90_000;
const BUDGET_KEY = "te-api-budget";
const COOLDOWN_KEY = "te-api-cooldown";

interface SharedBudget {
  start: number;
  used: number;
}

let tokens = BURST;
let lastRefill = Date.now();
let localCooldownUntil = 0;
let chain: Promise<void> = Promise.resolve();

type Listener = (until: number) => void;

const listeners = new Set<Listener>();

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function readJson<T>(key: string): T | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    return;
  }
}

function cooldownUntil(): number {
  const shared = readJson<{ until: number }>(COOLDOWN_KEY)?.until ?? 0;

  return Math.max(localCooldownUntil, shared);
}

function takeSharedSlot(): number {
  if (typeof window === "undefined") return 0;

  const now = Date.now();
  const budget = readJson<SharedBudget>(BUDGET_KEY);
  const window_ = budget && now - budget.start < WINDOW_MS ? budget : { start: now, used: 0 };

  if (window_.used < SHARED_QUOTA) {
    writeJson(BUDGET_KEY, { start: window_.start, used: window_.used + 1 });
    return 0;
  }

  return window_.start + WINDOW_MS - now;
}

function refill(): void {
  const now = Date.now();

  tokens = Math.min(BURST, tokens + ((now - lastRefill) * SHARED_QUOTA) / WINDOW_MS);
  lastRefill = now;
}

async function acquire(): Promise<void> {
  const ticket = chain.then(async () => {
    for (;;) {
      const now = Date.now();
      const pausedUntil = cooldownUntil();

      if (pausedUntil > now) {
        await sleep(pausedUntil - now);
        continue;
      }

      refill();

      if (tokens < 1) {
        await sleep(Math.ceil(((1 - tokens) * WINDOW_MS) / SHARED_QUOTA));
        continue;
      }

      const sharedWait = takeSharedSlot();

      if (sharedWait > 0) {
        await sleep(sharedWait);
        continue;
      }

      tokens -= 1;
      return;
    }
  });

  chain = ticket.catch(() => {});

  return ticket;
}

export function noteRateLimited(retryAfterSeconds?: number | string | null): void {
  const parsed = Number(retryAfterSeconds);
  const waitMs = Number.isFinite(parsed) && parsed > 0 ? parsed * 1000 : WINDOW_MS;
  const until = Date.now() + Math.min(MAX_COOLDOWN_MS, Math.max(MIN_COOLDOWN_MS, waitMs));

  localCooldownUntil = until;
  tokens = 0;
  lastRefill = Date.now();
  writeJson(COOLDOWN_KEY, { until });

  listeners.forEach((listener) => listener(until));
}

export function rateLimitedFor(): number {
  return Math.max(0, cooldownUntil() - Date.now());
}

export function onRateLimited(listener: Listener): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

export async function runBackground<T>(task: () => Promise<T>): Promise<T> {
  await acquire();

  return task();
}

export async function runForeground<T>(task: () => Promise<T>): Promise<T> {
  const paused = rateLimitedFor();

  if (paused > 0) await sleep(paused);

  takeSharedSlot();

  return task();
}
